import LogRocket from "logrocket";
import React, { PropsWithChildren } from "react";

import useModel, {
  AsyncWorkflow,
  AsyncWorkflowDispatch,
} from "@brexhq/next/src/hooks/useModelHook";
import {
  createCompositeAnalytics,
  createSegmentAnalyticsWithDecorations,
  createLogRocketAnalytics,
  createNullAnalyticsApi,
  createDatadogAnalytics,
} from "@brexhq/next/src/telemetry/analyticsApi";
import {
  IntegrationsFlags,
  InitializedSegmentClient,
  AnalyticsApi,
} from "@brexhq/next/src/telemetry/types";
import { Winda } from "@brexhq/next/src/utils/Winda";
import { getSegmentDestinationsFromCookies } from "@brexhq/next/src/utils/analytics";
import noop from "@brexhq/next/src/utils/noop";
import { isDev as _isDev } from "@brexhq/shared/utils/env/isDevEnv";

const segmentDestinations = getSegmentDestinationsFromCookies();

export class AnalyticsState {
  static withRestrictedCookieValues() {
    return new AnalyticsState(segmentDestinations);
  }
  /**
   * Used for logging experiment analytics. See Optimizely analyticsApi impl
   */
  eventLog: Array<string> = [];

  constructor(public analyticsIntegrations: IntegrationsFlags) {}
}

export enum AnalyticsActionType {
  UpdateAnalyticsIntegrations,
  AnalyticsEventDispatched,
}

export class UpdateAnalyticsIntegrationsAction {
  public readonly type = AnalyticsActionType.UpdateAnalyticsIntegrations;
  constructor(public readonly payload: IntegrationsFlags) {}
}

export class AnalyticsEventDispatchedAction {
  public readonly type = AnalyticsActionType.AnalyticsEventDispatched;
  public readonly meta: {
    isDev: boolean;
  };
  constructor(public readonly payload: string, isDev: boolean = _isDev) {
    this.meta = {
      isDev,
    };
  }
}

export type AnalyticsWorkflow = AsyncWorkflow<AnalyticsAction, AnalyticsState>;

export type AnalyticsAction =
  | UpdateAnalyticsIntegrationsAction
  | AnalyticsEventDispatchedAction;

export const analyticsReducer: React.Reducer<
  AnalyticsState,
  AnalyticsAction
> = (state: AnalyticsState, action: AnalyticsAction): AnalyticsState => {
  switch (action.type) {
    case AnalyticsActionType.UpdateAnalyticsIntegrations:
      return { ...state, analyticsIntegrations: action.payload };
    case AnalyticsActionType.AnalyticsEventDispatched:
      if (!action.meta.isDev) return state;
      return {
        ...state,
        eventLog: [...state.eventLog, action.payload],
      };
    default:
      return state;
  }
};

interface ContextProps {
  initialState: AnalyticsState;
  segmentClient: InitializedSegmentClient;
  logRocketInstance: typeof LogRocket;
}

interface AnalyticsContextValue {
  state: AnalyticsState;
  dispatch: AsyncWorkflowDispatch<AnalyticsState, AnalyticsAction>;
  analyticsApi: AnalyticsApi;
}

export const initialState = AnalyticsState.withRestrictedCookieValues();

export const AnalyticsContext = React.createContext<AnalyticsContextValue>({
  state: initialState,
  dispatch: noop,
  analyticsApi: createNullAnalyticsApi(),
});

export const AnalyticsContextProvider: React.FC<
  PropsWithChildren<ContextProps>
> = ({ segmentClient, children, logRocketInstance }) => {
  const [state, dispatch] = useModel(analyticsReducer, initialState);
  //TODO: may need this for statsig set up
  // const anonymousId = getOrCreateAnonymousId(anonymousIdStorageKey);

  // This useEffect is here to observe an event that is fired from the
  // airgap.js callback, so we can then do other react logic. We can't
  // dispatch reducer events directly as that script must be called inside html.js
  React.useEffect(() => {
    if (!Winda.isPresent) return;

    const handleConsentChange = () => {
      const segmentDestinations = getSegmentDestinationsFromCookies();
      dispatch(new UpdateAnalyticsIntegrationsAction(segmentDestinations));
    };

    window.airgap?.addEventListener?.("consent-change", handleConsentChange);
    return () =>
      window.airgap?.removeEventListener?.(
        "consent-change",
        handleConsentChange,
      );
    // Disabling because we want this to run once and only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const analyticsApi = React.useMemo(
    () =>
      createCompositeAnalytics({
        isDisabled: () => Winda.isDataDogBot,
        children: [
          createSegmentAnalyticsWithDecorations(segmentClient),
          createLogRocketAnalytics(logRocketInstance),
          createDatadogAnalytics(),
        ],
      }),
    [logRocketInstance, segmentClient],
  );

  const value = {
    state,
    dispatch,
    analyticsApi,
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};
